import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { BoostModal } from '../components/BoostModal';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import {
  Package,
  DollarSign,
  MapPin,
  Tag,
  FileText,
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  Trash2,
  Save,
  X,
  Sparkles,
  TrendingUp,
  Check,
  Laptop,
  Sofa,
  Shirt,
  Car,
  Dumbbell,
  Home,
  Palette,
  BookOpen,
  Wrench,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from '../lib/toast';
import { MultiPhotoUploader } from '../components/MultiPhotoUploader';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_ICONS = {
  electronics: Laptop,
  furniture: Sofa,
  clothing: Shirt,
  vehicles: Car,
  sports: Dumbbell,
  home: Home,
  collectibles: Palette,
  books: BookOpen,
  tools: Wrench,
  other: Package,
};

const CATEGORIES = [
  { id: 'electronics', name: 'Electronics' },
  { id: 'furniture', name: 'Furniture' },
  { id: 'clothing', name: 'Clothing & Fashion' },
  { id: 'vehicles', name: 'Vehicles & Parts' },
  { id: 'sports', name: 'Sports & Outdoors' },
  { id: 'home', name: 'Home & Garden' },
  { id: 'collectibles', name: 'Collectibles & Art' },
  { id: 'books', name: 'Books & Media' },
  { id: 'tools', name: 'Tools & Equipment' },
  { id: 'other', name: 'Other' },
];

const CONDITIONS = [
  { id: 'like_new', name: 'Like New', color: 'text-[#059669]', bg: 'bg-[#059669]/10' },
  { id: 'excellent', name: 'Excellent', color: 'text-[#059669]', bg: 'bg-[#059669]/10' },
  { id: 'good', name: 'Good', color: 'text-[#C9956C]', bg: 'bg-[#C9956C]/10' },
  { id: 'fair', name: 'Fair', color: 'text-[#D97706]', bg: 'bg-[#D97706]/10' },
  { id: 'poor/needs repair', name: 'Poor/Needs Repair', color: 'text-[#DC2626]', bg: 'bg-[#DC2626]/10' },
];

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=300&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=300&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&h=300&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-fffbd4f9b0db?w=400&h=300&fit=crop',
  home: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  collectibles: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
  books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=300&fit=crop',
  tools: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=300&fit=crop',
};

// Helper function to normalize image URLs
// Handles both string URLs and new object format {ref, url, expires_at}
const normalizeImageUrl = (url) => {
  if (!url) return null;
  // Handle new image object format
  if (typeof url === 'object' && url.url) {
    return url.url;
  }
  // Handle string URLs
  if (typeof url !== 'string') return null;
  if (url.startsWith('https://images.unsplash.com')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) return `${API_BASE_URL}${match[0]}`;
  }
  return url;
};

export default function InventoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { location: userLocation } = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [item, setItem] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [isChangingLocation, setIsChangingLocation] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    estimated_value: '',
    condition: '',
    is_public: false,
    is_firm: false,
    is_free: false,
    images: [],
    primary_image_index: 0,
    tags: '',
    location: null,
    location_name: '',
  });

  useEffect(() => {
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await api.get(`/inventory/${id}`);
      const itemData = response.data;
      setItem(itemData);
      setFormData({
        title: itemData.title || '',
        description: itemData.description || '',
        category: itemData.category || '',
        estimated_value: itemData.estimated_value?.toString() || '',
        condition: itemData.condition || '',
        is_public: itemData.is_public || false,
        is_firm: itemData.is_firm || false,
        is_free: itemData.is_free || false,
        images: itemData.images || [],
        primary_image_index: itemData.primary_image_index || 0,
        tags: itemData.tags?.join(', ') || '',
        location: itemData.location || null,
        location_name: itemData.location_name || '',
      });
    } catch (error) {
      console.error('Failed to fetch item:', error);
      toast.error('Failed to load item');
      navigate('/dashboard/owner');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.condition) {
      toast.error('Please select the item condition');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        estimated_value: parseFloat(formData.estimated_value),
        condition: formData.condition,
        is_public: formData.is_public,
        is_firm: formData.is_free ? false : formData.is_firm,
        is_free: formData.is_free,
        price: formData.is_free ? 0 : undefined,
        images: formData.images,
        primary_image_index: formData.primary_image_index,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        location: formData.location,
        location_name: formData.location_name,
      };

      await api.put(`/inventory/${id}`, payload);
      toast.success('Item updated successfully!');
      navigate('/dashboard/owner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle location selection from autocomplete
  const handleLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      location: { lat: locationData.lat, lng: locationData.lng },
      location_name: locationData.name
    }));
  };
  
  // Reset location to user's default location
  const handleUseMyLocation = () => {
    if (userLocation) {
      setFormData(prev => ({
        ...prev,
        location: { lat: userLocation.lat, lng: userLocation.lng },
        location_name: userLocation.name
      }));
      toast.success('Location set to your default');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/inventory/${id}`);
      toast.success('Item deleted successfully');
      navigate('/dashboard/owner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--theme-text-primary)]" />
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-12">
          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">Item not found</h1>
          <Button 
            onClick={() => navigate('/dashboard/owner')}
            className="mt-4 bg-[#1E3A5F] hover:bg-[#2D4A6F] rounded-full"
          >
            Back to Inventory
          </Button>
        </div>
      </Layout>
    );
  }

  const normalizedImage = normalizeImageUrl(item.images?.[0]);
  const imageUrl = (!imgError && normalizedImage) || CATEGORY_IMAGES[item.category] || CATEGORY_IMAGES.other;

  const isPaymentPending = !item.is_public
    && item.status === 'available'
    && item.vehicle_details?.intended_plus === true
    && item.vehicle_details?.listing_tier === 'basic';

  const handleRetryPayment = async () => {
    try {
      await api.post(`/inventory/${id}/purchase-plus`);
      toast.success('Payment successful — your Plus listing is live!');
      fetchItem();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Payment failed. Please check your payment method and try again.');
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Payment retry banner */}
        {isPaymentPending && (
          <div className="mb-4 p-4 rounded-xl border-2 border-red-400 bg-red-50 dark:bg-red-900/10">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-600 dark:text-red-400">Payment Required</p>
                  <p className="text-sm text-red-500 dark:text-red-400/80">Your Plus listing is saved as a draft. Complete payment to publish.</p>
                </div>
              </div>
              <Button
                onClick={handleRetryPayment}
                className="bg-red-500 hover:bg-red-600 text-white flex-shrink-0"
              >
                Pay $20 &amp; Publish
              </Button>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/dashboard/owner')} 
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Inventory
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[var(--theme-hover)] flex items-center justify-center">
                <Package className="w-7 h-7 text-[var(--theme-text-primary)]" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                  Edit Item
                </h1>
                <p className="text-[var(--theme-text-secondary)] mt-1">
                  Update your item details
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Boost Button */}
              <Button
                type="button"
                onClick={() => setShowBoostModal(true)}
                className={`rounded-full ${
                  item.boost_tier
                    ? 'bg-[#C9956C]/20 text-[#C9956C] border border-[#C9956C]/50 hover:bg-[#C9956C]/30'
                    : 'bg-[#1E3A5F] hover:bg-[#16304f] text-white'
                }`}
                data-testid="boost-item-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {item.boost_tier ? 'Boosted ✓' : 'Boost Listing'}
              </Button>
              
              {/* Delete Button */}
              <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-full dark:border-red-500/50 dark:hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[var(--theme-text-primary)]">Delete this item?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[var(--theme-text-secondary)]">
                    This action cannot be undone. This will permanently delete &quot;{item.title}&quot; from your items.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full border-[var(--theme-border)] text-[var(--theme-text-primary)]">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-red-500 hover:bg-red-600 rounded-full text-white"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            </div>
          </div>
        </div>

        {/* Item Preview Image */}
        <div className="mb-6 p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 rounded-xl overflow-hidden flex-shrink-0 border border-[var(--theme-border)] relative">
              <img 
                src={imageUrl} 
                alt={item.title}
                onError={() => setImgError(true)}
                className="w-full h-full object-cover"
              />
              {/* Views Badge */}
              <div className="absolute bottom-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-white text-xs">
                <Eye className="w-3 h-3" />
                <span>{item.views || 0}</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-[var(--theme-text-primary)] text-lg">{item.title}</h3>
                {item.boost_tier && (
                  <span className="px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1 bg-[#C9956C] text-white">
                    <TrendingUp className="w-3 h-3" />
                    {item.boost_tier === 'regional' ? 'REGIONAL' : item.boost_tier === 'metro' ? 'METRO' : 'LOCAL'} BOOST
                  </span>
                )}
              </div>
              <p className="text-sm text-[var(--theme-text-secondary)] mt-1 line-clamp-2">{item.description}</p>
              <div className="flex items-center gap-4 mt-3">
                <span className="text-lg font-bold text-[#059669]">
                  ${item.estimated_value?.toLocaleString()}
                </span>
                {item.estimated_rrp_new && (
                  <span className="text-sm text-[var(--theme-text-muted)] line-through">
                    ${item.estimated_rrp_new?.toLocaleString()} RRP
                  </span>
                )}
              </div>
              {item.boost_tier && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-3 text-xs text-[var(--theme-text-muted)]">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {item.boost_impressions ?? 0} impressions
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3 text-[#C9956C]" />
                      {item.boost_views ?? 0} boost views
                    </span>
                  </div>
                  {(item.boost_expires_at || item.boost_expiry) && (
                    <p className="text-xs text-[#C9956C]">
                      Expires: {new Date(item.boost_expires_at || item.boost_expiry).toLocaleDateString('en-AU', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-6">
          {/* Item Details */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-[var(--theme-text-primary)]" />
              Item Details
            </h2>
            
            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                Title <span className="text-[#DC2626]">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Green Leather 2-Seater Couch"
                className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-12"
                required
                data-testid="edit-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                Category <span className="text-[#DC2626]">*</span>
              </label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-12" data-testid="edit-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                  {CATEGORIES.map((cat) => {
                    const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                    return (
                      <SelectItem key={cat.id} value={cat.id} className="hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]">
                        <span className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          <span>{cat.name}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                Description <span className="text-[#DC2626]">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the item - brand, model, size, color, any defects or notable features..."
                className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] min-h-[100px]"
                required
                data-testid="edit-description-input"
              />
            </div>
          </div>

          {/* Photos */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-[#C9956C]" />
              Photos
            </h2>
            <MultiPhotoUploader
              images={formData.images}
              primaryIndex={formData.primary_image_index}
              onImagesChange={(images) => setFormData(prev => ({ ...prev, images }))}
              onPrimaryChange={(index) => setFormData(prev => ({ ...prev, primary_image_index: index }))}
              maxImages={6}
              api={api}
            />
          </div>

          {/* Value & Condition */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#059669]" />
              Value & Condition
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                  Estimated Value <span className="text-[#DC2626]">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-secondary)] font-medium">$</span>
                    <Input
                      type="number"
                      value={formData.is_free ? '0' : formData.estimated_value}
                      onChange={(e) => !formData.is_free && setFormData(prev => ({ ...prev, estimated_value: e.target.value }))}
                      placeholder="What would you sell it for?"
                      min="0"
                      className={`bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] pl-8 h-12 ${formData.is_free ? 'opacity-50' : ''}`}
                      required
                      disabled={formData.is_free}
                      data-testid="edit-value-input"
                    />
                  </div>
                  {/* FREE toggle */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      is_free: !prev.is_free,
                      is_firm: prev.is_free ? prev.is_firm : false,
                    }))}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 font-bold text-sm transition-all ${
                      formData.is_free
                        ? 'bg-[#059669] border-[#059669] text-white'
                        : 'bg-transparent border-[#D4CCC2] text-[var(--theme-text-muted)] hover:border-[#059669] hover:text-[#059669]'
                    }`}
                    data-testid="edit-free-toggle"
                  >
                    FREE
                  </button>
                  {!formData.is_free && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_firm: !prev.is_firm }))}
                      className="flex-shrink-0 flex items-center gap-2 cursor-pointer group"
                      data-testid="firm-price-toggle"
                    >
                      <span className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                        formData.is_firm ? 'bg-[#1E3A5F] border-[#1E3A5F]' : 'border-[#D4CCC2] group-hover:border-[#1E3A5F]/50'
                      }`}>
                        {formData.is_firm && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <div>
                        <span className="block text-sm font-medium text-[var(--theme-text-primary)]">Firm price</span>
                        <span className="block text-xs text-[var(--theme-text-muted)]">(no offers below)</span>
                      </div>
                    </button>
                  )}
                </div>
                {formData.is_free && item?.expires_at && (
                  <p className="mt-2 text-xs text-[#059669]">
                    Free listing — expires {new Date(item.expires_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                  Condition <span className="text-[#DC2626]">*</span>
                </label>
                <Select 
                  value={formData.condition} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, condition: value }))}
                >
                  <SelectTrigger className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-12" data-testid="edit-condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                    {CONDITIONS.map((cond) => (
                      <SelectItem key={cond.id} value={cond.id} className="hover:bg-[var(--theme-hover)]">
                        <span className={cond.color}>{cond.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-4">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#C9956C]" />
              Item Location
            </h2>
            
            {/* Current location display with change button */}
            {!isChangingLocation ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--theme-hover)]">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#059669]" />
                    <span className="text-[var(--theme-text-primary)] font-medium">
                      {formData.location_name || 'No location set'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsChangingLocation(true)}
                    className="px-3 py-1.5 text-sm font-medium text-[#1E3A5F] bg-white border border-[var(--theme-border)] rounded-full hover:bg-[var(--theme-hover)] transition-colors"
                  >
                    Change
                  </button>
                </div>
                
                {/* Use my default location button */}
                {userLocation && formData.location_name !== userLocation.name && (
                  <button
                    type="button"
                    onClick={handleUseMyLocation}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#1E3A5F] bg-[#F8F5F0] border border-[#C9956C] rounded-full hover:bg-[#C9956C]/10 transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    Use My Default Location ({userLocation.name})
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <LocationAutocomplete
                  value={formData.location_name}
                  onChange={(name) => setFormData(prev => ({ ...prev, location_name: name }))}
                  onSelect={(loc) => {
                    handleLocationSelect(loc);
                    setIsChangingLocation(false);
                  }}
                  placeholder="Search suburb or postcode..."
                />
                <button
                  type="button"
                  onClick={() => setIsChangingLocation(false)}
                  className="text-sm text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Visibility */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-4">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              {formData.is_public ? <Eye className="w-5 h-5 text-[#059669]" /> : <EyeOff className="w-5 h-5 text-[#C9956C]" />}
              Visibility
            </h2>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--theme-hover)]">
              <div className="flex-1">
                <label className="font-medium text-[var(--theme-text-primary)]">
                  {formData.is_public ? 'For Sale' : 'Unlisted'}
                </label>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                  {formData.is_public 
                    ? 'Listed in the marketplace. Anyone can find and buy this item.' 
                    : 'Not visible in the marketplace, but matches with Wants.'}
                </p>
              </div>
              <Switch
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
              />
            </div>
            
            {!formData.is_public && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#C9956C]/10 text-sm">
                <Sparkles className="w-4 h-4 text-[#C9956C] mt-0.5 flex-shrink-0" />
                <span className="text-[var(--theme-text-secondary)]">
                  <strong className="text-[#C9956C]">Unlisted:</strong> This item will still match with buyers' Wants, but won't appear in marketplace search.
                </span>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button 
              type="button"
              variant="outline"
              className="flex-1 border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-hover)] rounded-full h-14"
              onClick={() => navigate('/my-items')}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-[2] bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full h-14 text-lg font-semibold"
              disabled={saving}
              data-testid="edit-save-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      
      {/* Boost Modal */}
      <BoostModal 
        item={item}
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        onSuccess={fetchItem}
      />
    </Layout>
  );
}
